import { MouseClickRightSvg } from "@lhd/assets";
import { IData } from "../hook";
import {
  BaseGridCell,
  CellStyle,
  CellType,
  CellTypeEnum,
  ColumnProps,
  DynamicRow,
  GridCell,
  NonEditableField,
  OrgNodes,
  SalaryPermissionConfigs,
} from "../../../../types/provision.type";
import { useMemo } from "react";
import { useDeepCompareMemoize } from "@lhd/hooks";
import { get, isEmpty } from "lodash";
import Decimal from "decimal.js";
import { OptionType } from "@silevis/reactgrid";
import { ReceivedProps } from "./hook";

export const MONTHLY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const HIGHLIGHT_EMPTY_FIELDS: string[] = ["unit_price", "number"];
const COLORFUL_FIELDS: string[] = ["year_cycle", "provision_amount", "amount"];

export const FIELD_REQUIRED = [
  "month",
  "worker_id",
  "provision_type",
  "provision_item_id",
  "unit_price",
  "number",
];

export const provisionOptions = [
  {
    value: "provision",
    label: "支給項目",
  },
  {
    value: "allowance",
    label: "手当項目",
  },
  {
    value: "arrangement",
    label: "調整項目",
  },
  {
    value: "bonus",
    label: "賞与項目",
  },
];

export const unitOptionsDefault = [
  { label: "時間", value: "時間" },
  { label: "式", value: "式" },
  { label: "月", value: "月" },
];

export const useColumns = (additionalField: OrgNodes[], size?: any) => {
  return useMemo(
    () => [
      {
        columnId: "index",
        label: "No.",
        type: "text",
        width: 40,
        icon: <></>,
      },
      {
        columnId: "year_cycle",
        label: "年度",
        type: "text",
        width: 100,
        icon: <></>,
      },
      {
        columnId: "month",
        label: "月度",
        type: "text",
        width: 100,
        icon: <></>,
      },
      {
        columnId: "worker_id",
        label: "社員",
        type: "text",
        width: 215,
        icon: <MouseClickRightSvg size={size} />,
      },
      ...additionalField.map((item) => ({
        columnId: `${item.name}-${item.id}`,
        label: item.name,
        type: "text",
        width: 120,
        values: null,
        icon: <MouseClickRightSvg size={size} />,
      })),
      {
        columnId: "provision_type",
        label: "分類",
        type: "text",
        width: 120,
        icon: <MouseClickRightSvg size={size} />,
      },
      {
        columnId: "provision_item_id",
        label: "項目",
        type: "text",
        width: 120,
        icon: <MouseClickRightSvg size={size} />,
      },
      {
        columnId: "unit_price",
        label: "単価",
        type: "numberCurrency",
        width: 150,
        icon: <></>,
      },
      {
        columnId: "number",
        label: "数量",
        type: "number",
        width: 150,
        icon: <></>,
      },
      {
        columnId: "unit",
        label: "単位",
        type: "text",
        width: 150,
        icon: <MouseClickRightSvg size={size} />,
      },
      {
        columnId: "memo",
        label: "M E Ｍ Ｏ",
        type: "text",
        width: 200,
        icon: <></>,
      },
      {
        columnId: "amount",
        label: "発生総額",
        type: "numberCurrency",
        width: 150,
        icon: <></>,
      },
      {
        columnId: "contract_amount",
        label: "請負額",
        type: "numberCurrency",
        width: 150,
        icon: <></>,
      },
      // {
      //   columnId: "contractor",
      //   label: "請負支払元",
      //   type: "text",
      //   width: 150,
      //   icon: <MouseClickRightSvg />,
      // },
      {
        columnId: "provision_amount",
        label: "給与額",
        type: "numberCurrency",
        width: 150,
        icon: <></>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompareMemoize([additionalField, size]),
  );
};

export const handleData = (data: IData[], orgNodes: OrgNodes[]) => {
  const lengthData = data?.length;
  const lastIndex = data?.[lengthData - 1]?.id
    ? (data?.[lengthData - 1]?.id as number) + 1
    : lengthData;

  return [
    ...data,
    ...Array.from(Array(data.length > 300 ? 0 : 300 - data.length).keys()).map(
      (_, index) => {
        return {
          id: index + lastIndex,
          ...emptyData,
          ...orgNodes.reduce(
            (curr: any, arr: any) => ({
              ...curr,
              [`${arr.name}-${arr.id}`]: "",
            }),
            {},
          ),
        };
      },
    ),
  ];
};

export const emptyData = {
  division_id: "",
  branch_id: "",
  month: "",
  year_cycle: "",
  worker_id: "",
  provision_type: "",
  provision_item_id: "",
  department_id: "",
  unit_price: "",
  number: null,
  unit: "",
  memo: "",
  amount: "",
  contract_amount: "",
  contractor: "",
  provision_amount: "",
  deduction: null,
  achievement: null,
  expense: null,
  isCreate: true,
};

export const checkInvalid = (
  data: IData,
  fields?: string[],
  countFieldValidate?: number,
) => {
  let result = false;
  let haveData = false;
  let countFieldInvalid = 0;
  if (fields) {
    fields.forEach((fieldItem) => {
      if (!data?.[fieldItem as keyof IData]) {
        result = true;
        countFieldInvalid = countFieldInvalid + 1;
      } else {
        haveData = true;
      }
    });
  } else {
    Object.keys(data).forEach((keyItem: string) => {
      if (!data?.[keyItem as keyof IData]) {
        result = true;
      } else {
        haveData = true;
      }
    });
  }
  return {
    invalid:
      countFieldValidate === undefined
        ? result
        : result && fields && countFieldInvalid === countFieldValidate,
    haveData: data?.isDeleted ? false : haveData,
  };
};

export const checkIncludeValue = (obj: any) => {
  let isValid = false;

  FIELD_REQUIRED.forEach((field: any) => {
    if (isValid) return;
    isValid = get(obj, field) ? true : false;
  });

  return isValid;
};

// Helper function to safely convert value to number
export const toNumber = (value?: any): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

export const numberFormatter = new Intl.NumberFormat("ja-JP", {
  maximumFractionDigits: 2,
});

export const dateFormatter = new Intl.DateTimeFormat("ja-JP", {
  month: "long",
  day: "numeric",
});

export const getBackgroundColor = (
  columnId: string,
  idx: number,
  obj: DynamicRow,
  haveData: boolean,
) => {
  if (HIGHLIGHT_EMPTY_FIELDS.includes(columnId) && !obj[columnId] && haveData) {
    return "#ffa5a5";
  }
  if (COLORFUL_FIELDS.includes(columnId)) {
    return idx % 2 ? "#01A2E8" : "#99D9EA";
  }
  return idx % 2 ? "#efefef" : "#fff";
};

export const getFieldKeys = (obj: Record<string, unknown>) =>
  Object.keys(obj).filter(
    (i: string) => !["index", "id", "isCreate", "sort_index"].includes(i),
  );

const getValidator =
  (
    item: ColumnProps,
    obj: DynamicRow,
    haveData: boolean,
    additionalField: OrgNodes[],
    validationFunctions: Record<string, (value: string) => boolean>,
    props: ReceivedProps,
  ) =>
  (cellValue?: string | number | null) => {
    if (!cellValue) {
      switch (item.columnId) {
        case "month":
        case "worker_id":
        case "provision_type":
        case "provision_item_id":
          return haveData ? !!obj?.[item.columnId] : true;
        default:
          return true;
      }
    }
    if (item.columnId in validationFunctions) {
      return validationFunctions[
        item.columnId as keyof typeof validationFunctions
      ](String(cellValue));
    }
    const fields = additionalField?.map((f) => `${f.name}-${f.id}`);
    if (fields?.includes(item.columnId as string)) {
      const result = props.orgData?.data
        ?.find(
          (org: any) => org?.id == (item?.columnId as string)?.split("-")?.[1],
        )
        ?.org_nodes?.map((n: any) => n.name);
      return result?.includes(String(cellValue).split("\r")[0]) ?? true;
    }
    return true;
  };

const handleEditable = (
  column: ColumnProps,
  record: DynamicRow,
  workerOptions: OptionType[],
  salaryConfigs: SalaryPermissionConfigs,
) => {
  const workerIdString = record?.worker_id;

  const workerId = (() => {
    if (!workerIdString) return "";
    const result = workerOptions?.find(
      (i) => i?.label === workerIdString,
    )?.value;

    return Number(result);
  })();

  const salaryEdit = [
    ...(salaryConfigs?.data?.salary_edit || []),
    ...(salaryConfigs?.data?.salary_delete || []),
  ];

  const salaryView = salaryConfigs?.data?.salary_view;
  // const salaryView = salaryConfigs?.data?.salary_view.filter(
  //   (id: string) => id !== auth?.workers?.[0]?.id,
  // );
  const isView = workerId && !!salaryView?.includes(workerId);
  const isEdit = workerId && !!salaryEdit?.includes(workerId);

  if (column?.editTable) return true;

  if (isEdit || salaryEdit?.includes("all")) return false;

  if (
    //  isEmpty(salaryView) ||
    isEmpty(salaryEdit) ||
    isView
  )
    return true;

  return false;
};

export const createCell = (
  item: ColumnProps,
  obj: DynamicRow,
  idx: number,
  originalData: IData[],
  haveData: boolean,
  additionalField: OrgNodes[],
  validationFunctions: Record<string, (value: string) => boolean>,
  props: ReceivedProps,
  columnTypeValueMap: Record<string, string>,
  salaryConfigs: SalaryPermissionConfigs,
): GridCell => {
  const columnId = item.columnId as string;
  const type = item.type as CellType;
  const workerOptions = props.workerOptions || [];

  const cellStyle: CellStyle = {
    ...(type === "numberCurrency" && { justifyContent: "end" }),
    ...(["amount", "provision_amount", "year_cycle"].includes(columnId) && {
      color: "#fff",
    }),
    backgroundColor: getBackgroundColor(columnId, idx, obj, haveData),
  };

  const baseCell: BaseGridCell = {
    columnId: item.columnId,
    provisionId: obj?.provisionId ?? null,
    sort_index: (obj?.sort_index as number) ?? null,
    type,
    style: cellStyle,
    nonEditable:
      [
        NonEditableField.INDEX,
        NonEditableField.YEAR_CYCLE,
        NonEditableField.AMOUNT,
        NonEditableField.PROVISION_AMOUNT,
      ].includes(columnId as NonEditableField) ||
      idx === originalData.length - 1
        ? true
        : handleEditable(item, obj, workerOptions, salaryConfigs),
    [columnTypeValueMap[type]]: obj[item.columnId],
  };

  let cell: GridCell;
  switch (type) {
    case CellTypeEnum.TEXT:
    case CellTypeEnum.NUMBER_CURRENCY:
    case CellTypeEnum.HEADER_ICON:
      cell = {
        ...baseCell,
        type,
        text: String(obj[item.columnId] ?? ""),
      };
      break;
    case CellTypeEnum.NUMBER:
      cell = {
        ...baseCell,
        type,
        value: toNumber(obj[item.columnId]),
        format: numberFormatter,
      };
      break;
    case CellTypeEnum.DATE:
      cell = {
        ...baseCell,
        type,
        format: dateFormatter,
      };
      break;
    default:
      cell = {
        ...baseCell,
        type: CellTypeEnum.TEXT,
        text: String(obj[item.columnId] ?? ""),
      };
      break;
  }

  cell.validator = getValidator(
    item,
    obj,
    haveData,
    additionalField,
    validationFunctions,
    props,
  );

  return cell;
};

export const convertNumberWithRounding = (
  value: number | string,
  negativeRoundingType?: string,
  positiveRoundingType?: string,
): number => {
  let decimal: Decimal;

  try {
    decimal = new Decimal(value);
  } catch (error) {
    return 0;
  }

  switch (decimal.isPositive()) {
    case true:
      switch (positiveRoundingType) {
        case "positive_ceil":
          return decimal.ceil().toNumber();
        case "positive_floor":
          return decimal.floor().toNumber();
        case "positive_round":
          return decimal.round().toNumber();
        default:
          return decimal.toNumber();
      }
    case false:
      switch (negativeRoundingType) {
        case "negative_ceil":
          return decimal.floor().toNumber();
        case "negative_floor":
          return decimal.ceil().toNumber();
        case "negative_round":
          return decimal.round().toNumber();
        default:
          return decimal.toNumber();
      }
    default:
      return decimal.toNumber();
  }
};

export const createDecimal = (value: any): Decimal => {
  try {
    return new Decimal(value || 0);
  } catch (error) {
    return new Decimal(0);
  }
};

export const addDecimal = (a: any, b: any): Decimal => {
  return createDecimal(a).add(createDecimal(b));
};

export const sumByField = (array: any[], field: string): Decimal => {
  return array.reduce((total, item) => {
    return addDecimal(total, item?.[field]);
  }, createDecimal(0));
};

export const sumByFieldToNumber = (array: any[], field: string): number => {
  return sumByField(array, field).toNumber();
};

export const sumByFieldToString = (array: any[], field: string): string => {
  return sumByField(array, field).toString();
};

export const calculateAmount = (
  unitPrice: any,
  number: any,
  negativeRoundingType?: string,
  positiveRoundingType?: string,
): string => {
  const unitPriceDecimal = createDecimal(unitPrice);
  const numberDecimal = createDecimal(number);

  if (unitPriceDecimal.isZero() || numberDecimal.isZero()) {
    return "";
  }

  const result = unitPriceDecimal.mul(numberDecimal);

  if (!negativeRoundingType && !positiveRoundingType) {
    return "";
  }

  const roundedResult = convertNumberWithRounding(
    result.toNumber(),
    negativeRoundingType,
    positiveRoundingType,
  ).toString();
  return roundedResult === "0" ? "" : roundedResult;
};

export const calculateProvisionAmount = (
  amount: any,
  contractAmount: any,
): string => {
  const amountDecimal = createDecimal(amount);
  const contractDecimal = createDecimal(contractAmount);

  if (amountDecimal.isZero() && contractDecimal.isZero()) {
    return "";
  }

  return amountDecimal.sub(contractDecimal).toString();
};

export const parseFormattedNumber = (value: any): number | null => {
  if (!value) return null;

  try {
    const cleanValue = value.toString().replaceAll(".", "").replaceAll(",", "");
    return createDecimal(cleanValue).toNumber();
  } catch (error) {
    return null;
  }
};

export const provision_sheet_special_field_convert_attr = [
  "worker_id",
  "division_id",
  "branch_id",
  "department_id",
  "provision_type",
  "provision_item_id",
  "contractor",
  "unit",
];
